<template>
  <div>
    <template v-for="(param, index) in parametersToRender">
      <div
        :key="param.name"
        class="cf-node-properties__field"
        :class="getFieldSize(param.name)"
      >
        <label
          v-if="!(param.type == 'sub_type' && !param.isOptional) || (param.type === 'list_multiselect')"
          class="control-label"
        >
          {{ param.displayName }} <span v-if="!param.isOptional">*</span>
          <i
            v-tooltip.top="param.description"
            class="fa fa-question-circle m-l-5"
            aria-hidden="true"
          />
        </label>

        <InputSelect
          v-if="param.type === 'list'"
          v-model="settingsCopy[param.name]"
          :options="param.values"
          :required="!param.isOptional"
          :placeholder="param.defaultValue"
          :error="getFieldError(propertyErrors, param.name)"
          @input="updateSettings"
        />

        <InputNumber
          v-else-if="param.type === 'number'"
          v-model="settingsCopy[param.name]"
          :required="!param.isOptional"
          :placeholder="param.defaultValue"
          :error="getFieldError(propertyErrors, param.name)"
          @input="inputChange"
        />

        <InputText
          v-else-if="param.type === 'string'"
          v-model="settingsCopy[param.name]"
          :required="!param.isOptional"
          :placeholder="param.defaultValue"
          :error="getFieldError(propertyErrors, param.name)"
          @input="inputChange"
        />

        <div
          v-else-if="param.type === 'bool'"
        >
          <div class="cf-node-properties__input">
            <nzToggle
              v-model="settingsCopy[param.name]"
              @input="updateSettings"
            />
          </div>
        </div>

        <div
          v-else-if="param.type === 'sub_type' && param.isOptional"
          class="cf-node-properties__input"
        >
          <nzToggle
            v-model="settingsCopy[param.name].isEnabled"
            @input="updateSettings"
          />
        </div>

        <div v-else-if="param.type === 'list_multiselect'">
          <div
            class="cf-node-properties__input m-b-15"
          >
            <MultipleToggle
              :value="settingsCopy[param.name]"
              :options="param.values"
              :default-value="param.defaultValue"
              @onToggle="$event => {
                settingsCopy[param.name] = $event.value;
                updateSettings();
              }"
            />
          </div>
          <div
            v-if="getFilteredParameters(linkedParameters, param.name).length > 0"
            class="row"
          >
            <SubTypeProps
              :value="settingsCopy"
              class="col-md-12"
              :parameters="getFilteredParameters(linkedParameters, param.name)"
              :property-errors="propertyErrors"
              :parent-parameter-name="param.name"
              @input="deepObject => updateNested(deepObject, getFilteredParameters(linkedParameters, param.name))"
            />
          </div>
        </div>
      </div>
      
      <AudioComponent
        v-if="showAudioParameter(param.name)"
        :key="`audio-${param.name}-${index}`"
        :params="param.parameters"
        :parent-setting="param.name"
        :errors="propertyErrors"
        :settings="settingsCopy"
        @update:settings="handleAudioSettingsUpdate"
      />

      <template
        v-if="!showAudioParameter(param.name) && param.type === 'sub_type' && settingsCopy[param.name].isEnabled"
      >
        <SubTypeProps
          :key="`subType-${index}`"
          v-model="settingsCopy[param.name]"
          :parameters="param.parameters"
          :property-errors="propertyErrors"
          :parent-parameter-name="param.name"
          @input="updateSettings"
        />
      </template>

      <template
        v-if="getFilteredParameters(linkedParameters, param.name).length > 0 && param.type === 'bool'"
      >
        <SubTypeProps
          v-if="getFilteredParameters(linkedParameters, param.name).length > 0"
          :key="`subType-${index}`"
          :value="settingsCopy"
          :parameters="getFilteredParameters(linkedParameters, param.name)"
          :property-errors="propertyErrors"
          :parent-parameter-name="param.name"
          :subgroup="false"
          @input="deepObject => updateNested(deepObject, getFilteredParameters(linkedParameters, param.name))"
        />
      </template>
    </template>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';

import SubTypeProps from './SubTypeProps';

import {
  getFieldError,
  getFieldSize,
} from '../../../helpers/node';
import { mapGetters } from 'vuex';

import InputText from './Form/InputText.vue';
import nzToggle from '@/core/components/nzToggle/nzToggle';
import InputNumber from './Form/InputNumber';
import InputSelect from './Form/InputSelect';
import AudioComponent from './AudioComponent.vue';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle';

export default {
  name: 'GenericProps',

  components: {
    InputText,
    nzToggle,
    InputNumber,
    InputSelect,
    AudioComponent,
    SubTypeProps,
    MultipleToggle
  },

  props: {
    nodeId: {
      type: String,
      default: ''
    },

    parameters: {
      type: Array,
      default: () => []
    },

    settings: {
      type: Object,
      default() {
        return {};
      }
    },

    audioParameters: {
      type: Array,
      default() {
        return [];
      }
    },

    optionalAudioParameters: {
      type: Array,
      default() {
        return [];
      }
    }
  },

  data() {
    return {
      settingsCopy: {}
    };
  },

  computed: {
    ...mapGetters('callFlows', [
      'nodeErrors'
    ]),

    propertyErrors() {
      return this.nodeErrors[this.nodeId]?.propertyErrors;
    },

    generalErrors() {
      return this.nodeErrors[this.nodeId]?.errors;
    },

    hasErrors() {
      return (this.generalErrors && this.generalErrors?.length) || (this.propertyErrors && this.propertyErrors?.length);
    },

    parametersToRender() {
      return this.parameters.filter(p => !p.linkedTo);
    },

    linkedParameters() {
      return this.parameters.filter(p => p.linkedTo && p.showIf);
    }
  },

  watch: {
    settings: {
      immediate: true,
      handler(newValue) {
        this.settingsCopy = cloneDeep(newValue);
      }
    }
  },

  methods: {
    getFieldError,
    getFieldSize,

    inputChange: debounce(function inputChange() {
      this.$emit('update:settings', this.settingsCopy);
    }, 500),

    updateSettings() {
      this.$emit('update:settings', this.settingsCopy);
    },

    handleAudioSettingsUpdate(settings) {
      this.settingsCopy = settings;
      this.updateSettings();
    },

    showAudioParameter(paramName) {
      if (this.audioParameters.includes(paramName)) {
        return true;
      }

      if (this.optionalAudioParameters.includes(paramName) && this.settingsCopy[paramName].isEnabled) {
        return true;
      }

      return false;
    },

    getFilteredParameters(parameters, parentParameterName) {
      return parameters.filter(p => p.linkedTo === parentParameterName && p.showIf.includes(String(this.settingsCopy[parentParameterName])));
    },

    // Only update the nested object properties that are shown. Used for safely updating the settingsCopy object
    updateNested(deepObject, childParameters) {
      const childParameterNames = childParameters.map(p => p.name);
      const updateObject = { ...deepObject };

      childParameterNames.forEach(name => {
        this.settingsCopy[name] = updateObject[name]; 
      });

      this.updateSettings();
    }
    
  },
};
</script>

<style>

</style>